import React, { useEffect, useMemo } from "react";
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Spin,
    Switch,
    Typography,
    message,
} from "antd";
import { BulbOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import {
    useGetSystemSettingsByCategoryQuery,
    useUpdateSystemSettingsBatchMutation,
} from "../../services/nestjs-base-api";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const KEYS = {
    enabled: "FIELD_SUGGESTIONS_ENABLED",
    primaryProvider: "FIELD_SUGGESTIONS_PRIMARY_PROVIDER",
    primaryModel: "FIELD_SUGGESTIONS_PRIMARY_MODEL",
    primaryTemperature: "FIELD_SUGGESTIONS_PRIMARY_TEMPERATURE",
    fallbackProvider: "FIELD_SUGGESTIONS_FALLBACK_PROVIDER",
    fallbackModel: "FIELD_SUGGESTIONS_FALLBACK_MODEL",
    fallbackTemperature: "FIELD_SUGGESTIONS_FALLBACK_TEMPERATURE",
    systemPrompt: "FIELD_SUGGESTIONS_SYSTEM_PROMPT",
    userPromptTemplate: "FIELD_SUGGESTIONS_USER_PROMPT_TEMPLATE",
};

const PROVIDERS = [
    { label: "Anthropic", value: "anthropic" },
    { label: "OpenAI", value: "openai" },
    { label: "Google", value: "google" },
];

/**
 * SYR-78 — Configure page for the Field Suggestions AI (maps ingested content
 * onto the structured profile fields). Reads/writes FIELD_SUGGESTIONS_* settings;
 * usage recorded under the 'field_suggestions' feature.
 */
export const FieldSuggestionsConfigurePage: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken || "";
    const [form] = Form.useForm();

    const { data, isLoading } = useGetSystemSettingsByCategoryQuery({
        jwtToken: jwt,
    });
    const [saveBatch, { isLoading: isSaving }] =
        useUpdateSystemSettingsBatchMutation();

    const settingsMap = useMemo(() => {
        const map: Record<string, string> = {};
        (data?.categories || []).forEach((cat) =>
            cat.settings.forEach((s) => {
                map[s.name] = s.value;
            })
        );
        return map;
    }, [data]);

    useEffect(() => {
        if (!data) return;
        form.setFieldsValue({
            enabled: settingsMap[KEYS.enabled] !== "false",
            primaryProvider: settingsMap[KEYS.primaryProvider] || "anthropic",
            primaryModel: settingsMap[KEYS.primaryModel] || "",
            primaryTemperature: parseFloat(
                settingsMap[KEYS.primaryTemperature] || "0.1"
            ),
            fallbackProvider: settingsMap[KEYS.fallbackProvider] || "openai",
            fallbackModel: settingsMap[KEYS.fallbackModel] || "",
            fallbackTemperature: parseFloat(
                settingsMap[KEYS.fallbackTemperature] || "0.1"
            ),
            systemPrompt: settingsMap[KEYS.systemPrompt] || "",
            userPromptTemplate: settingsMap[KEYS.userPromptTemplate] || "",
        });
    }, [data, settingsMap, form]);

    const onFinish = async (values: any) => {
        if (!values.userPromptTemplate?.includes("{source_text}")) {
            message.error("The user prompt template must include {source_text}.");
            return;
        }
        const settings = [
            { name: KEYS.enabled, value: values.enabled ? "true" : "false" },
            { name: KEYS.primaryProvider, value: values.primaryProvider },
            { name: KEYS.primaryModel, value: values.primaryModel },
            {
                name: KEYS.primaryTemperature,
                value: String(values.primaryTemperature),
            },
            { name: KEYS.fallbackProvider, value: values.fallbackProvider },
            { name: KEYS.fallbackModel, value: values.fallbackModel },
            {
                name: KEYS.fallbackTemperature,
                value: String(values.fallbackTemperature),
            },
            { name: KEYS.systemPrompt, value: values.systemPrompt },
            { name: KEYS.userPromptTemplate, value: values.userPromptTemplate },
        ];
        try {
            await saveBatch({ jwtToken: jwt, payload: { settings } }).unwrap();
            message.success("Field Suggestions AI settings saved");
        } catch (err: any) {
            message.error(err?.data?.message || "Could not save settings");
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: "center" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24, maxWidth: 900 }}>
            <Title level={3} style={{ marginTop: 0 }}>
                <BulbOutlined style={{ marginRight: 8 }} />
                Field Suggestions AI
            </Title>
            <Paragraph type="secondary">
                Reads ingested brand documents/scrapes and proposes values for the
                structured Brand Knowledge fields (positioning, products, customers,
                competitors, tone, goals), which the user reviews and appends.
                Returns JSON. Token usage is recorded under the{" "}
                <code>field_suggestions</code> feature.
            </Paragraph>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card style={{ marginBottom: 16 }}>
                    <Form.Item
                        name="enabled"
                        label="Enabled"
                        valuePropName="checked"
                        extra="When off, no field suggestions are offered after ingest."
                    >
                        <Switch />
                    </Form.Item>
                </Card>

                <Card title="Model" style={{ marginBottom: 16 }}>
                    <Space size="large" wrap>
                        <Form.Item name="primaryProvider" label="Primary provider">
                            <Select options={PROVIDERS} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="primaryModel" label="Primary model">
                            <Input style={{ width: 280 }} />
                        </Form.Item>
                        <Form.Item
                            name="primaryTemperature"
                            label="Primary temperature"
                        >
                            <InputNumber min={0} max={1} step={0.05} />
                        </Form.Item>
                    </Space>
                    <Space size="large" wrap>
                        <Form.Item name="fallbackProvider" label="Fallback provider">
                            <Select options={PROVIDERS} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item name="fallbackModel" label="Fallback model">
                            <Input style={{ width: 280 }} />
                        </Form.Item>
                        <Form.Item
                            name="fallbackTemperature"
                            label="Fallback temperature"
                        >
                            <InputNumber min={0} max={1} step={0.05} />
                        </Form.Item>
                    </Space>
                </Card>

                <Card title="Prompts" style={{ marginBottom: 16 }}>
                    <Form.Item
                        name="systemPrompt"
                        label="System prompt"
                        extra="Must instruct the model to return JSON keyed by the profile field names."
                    >
                        <TextArea rows={12} style={{ fontFamily: "monospace" }} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="The user prompt template must include the {source_text} placeholder."
                    />
                    <Form.Item name="userPromptTemplate" label="User prompt template">
                        <TextArea rows={4} style={{ fontFamily: "monospace" }} />
                    </Form.Item>
                </Card>

                <Button type="primary" htmlType="submit" loading={isSaving}>
                    Save
                </Button>
            </Form>
        </div>
    );
};
